<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  canEdit: { type: Boolean, default: false },
  isComplete: { type: Function, required: true },
  partyKind: { type: String, default: '' },
  progressPercent: { type: Number, default: null },
  saving: { type: Boolean, default: false },
  sections: { type: Array, default: () => [] },
});

const emit = defineEmits(['save']);
const { t } = useI18n();
const completedSections = computed(
  () => props.sections.filter(section => props.isComplete(section)).length
);
const derivedProgress = computed(() => {
  if (!props.sections.length) return 0;

  return Math.round((completedSections.value / props.sections.length) * 100);
});
const progress = computed(() => props.progressPercent ?? derivedProgress.value);
const company = computed(() => props.partyKind === 'pessoa_juridica');
const progressConnector = computed(() =>
  company.value
    ? t('SALES_PARTIES.REGISTRATION.PROGRESS_COMPANY_CONNECTOR')
    : t('SALES_PARTIES.REGISTRATION.PROGRESS_PERSON_CONNECTOR')
);
const requiredDocument = computed(() =>
  company.value
    ? t('SALES_PARTIES.DOCUMENTS.COMPANY_REQUIRED_HIGHLIGHT')
    : t('SALES_PARTIES.DOCUMENTS.PERSON_REQUIRED_HIGHLIGHT')
);
const completionPercent = computed(() =>
  t('SALES_PARTIES.COMMON.FULL_PERCENT')
);
const canComplete = computed(
  () => props.canEdit && progress.value === 100 && !props.saving
);
</script>

<template>
  <footer
    data-test="sales-party-registration-progress-footer"
    class="fixed bottom-0 left-[287px] right-0 z-20 hidden h-[82px] border-t border-[#DCE3E6] bg-white font-roboto min-[701px]:flex"
  >
    <div class="flex min-w-0 flex-1 items-center justify-between px-[30px]">
      <div class="mr-[10px] w-[70%] min-w-0 shrink-0">
        <strong class="text-xl leading-6 text-[#58656B]">
          {{ t('SALES_PARTIES.COMMON.PERCENT', { value: progress }) }}
        </strong>
        <div class="mt-2 h-1 overflow-hidden rounded-full bg-[#D9E2E5]">
          <span
            data-test="sales-party-registration-progress"
            class="block h-full rounded-full bg-[#009F94] transition-[width]"
            :style="{ width: `${progress}%` }"
          />
        </div>
        <p class="mt-2 truncate text-base text-[#464F53]">
          {{ t('SALES_PARTIES.REGISTRATION.PROGRESS_PREFIX') }}
          <strong>{{ completionPercent }}</strong>
          {{ progressConnector }}
          <strong>{{ requiredDocument }}</strong>
        </p>
      </div>

      <button
        data-test="sales-party-registration-complete"
        type="button"
        class="flex h-[60px] min-w-[191px] items-center justify-center rounded-[10px] bg-[#009F94] px-6 text-base font-bold text-white transition hover:bg-[#01655D] disabled:cursor-not-allowed disabled:opacity-50"
        :disabled="!canComplete"
        @click="emit('save')"
      >
        {{ t('SALES_PARTIES.ACTIONS.COMPLETE_REGISTRATION') }}
      </button>
    </div>
  </footer>
</template>
